import React, { useEffect, useState } from 'react'
import { Animated, StyleSheet, View, TouchableOpacity } from 'react-native'
import { useSelector } from 'react-redux'

import TaskItemTags from '../../TaskItemTags'
import TimeTagWrapper from '../../../Tags/TimeTagWrapper'
import CompletedTimeTag from '../../../Tags/CompletedTimeTag'
import CalendarTag from '../../../Tags/CalendarTag'
import Tags from '../../TagsArea/Tags'
import { shouldOnPressInput } from '../../Utils/TasksHelper'

export default function TaskTagsContainerByTime({
    task,
    projectId,
    isObservedTask,
    isToReviewTask,
    toggleSubTaskList,
    subtaskList,
    isSuggested,
    isActiveOrganizeMode,
    isPending,
    isLocked,
    highlightColor,
    anonymousGranted,
    accessGranted,
    taskTagsSection,
    forceTagsMobile,
    setTagsExpandedHeight,
    toggleModal,
    blockOpen,
    leadingVmStatusTag,
    leadingPriorityTag,
    trailingRoutingTag,
    inCommentPopup,
}) {
    const [showSummarizeTag, setShowSummarizeTag] = useState(false)
    const [widthInFullArea, setWidthInFullArea] = useState(0)
    const [widthInLeftArea, setWidthInLeftArea] = useState(0)
    const [widthInRightArea, setWidthInRightArea] = useState(0)
    const [expandTags, setExpandTags] = useState(false)
    const taskViewToggleSection = useSelector(state => state.taskViewToggleSection)
    const inOpenSection = taskViewToggleSection === 'Open'

    const onLayoutInExpandedTagsArea = e => {
        setTagsExpandedHeight(e.nativeEvent.layout.height)
    }

    const onLayoutInFullArea = e => {
        if (!showSummarizeTag) setWidthInFullArea(e.nativeEvent.layout.width)
    }

    const onLayoutInLeftArea = e => {
        if (!showSummarizeTag) setWidthInLeftArea(e.nativeEvent.layout.width)
    }

    const onLayoutInRightArea = e => {
        if (!showSummarizeTag) setWidthInRightArea(e.nativeEvent.layout.width)
    }

    useEffect(() => {
        if (!showSummarizeTag && widthInFullArea && widthInLeftArea && widthInRightArea) {
            if (widthInLeftArea + widthInRightArea > widthInFullArea) {
                setShowSummarizeTag(true)
            }
        }
    }, [showSummarizeTag, widthInFullArea, widthInLeftArea, widthInRightArea])

    return (
        <Animated.View style={[localStyles.taskTags, { backgroundColor: highlightColor }]}>
            <TouchableOpacity
                onPress={e => {
                    if (toggleModal && shouldOnPressInput(e, blockOpen)) toggleModal(e)
                }}
            >
                <View
                    ref={taskTagsSection}
                    onLayout={onLayoutInFullArea}
                    style={localStyles.innerTaskTags}
                    nativeID={`social_tags_${projectId}_${task.id}`}
                >
                    <View onLayout={onLayoutInLeftArea} style={localStyles.leftArea}>
                        {leadingVmStatusTag}
                        {task && task.time && !task.calendarData && (
                            <TimeTagWrapper projectId={projectId} task={task} />
                        )}
                        {/* A calendar task keeps its calendar tag once done so it stays recognizable as a
                            calendar event. Its completedTime is derived from the event slot itself
                            (getTaskCompletedTime), so the completed time tag would only duplicate it. */}
                        {task && task.calendarData && (
                            <CalendarTag calendarData={task.calendarData} containerStyle={{ marginRight: 8 }} />
                        )}
                        {!inOpenSection && task && task.completedTime && !task.calendarData && (
                            <CompletedTimeTag task={task} />
                        )}
                        {leadingPriorityTag}
                    </View>
                    <View onLayout={onLayoutInRightArea} style={{ flexDirection: 'row', alignItems: 'center' }}>
                        {/* AT-2453 — same move as the regular row: the routing badge belongs with the
                            trailing tags on the right, not in the leading area beside the time and
                            priority chips. It stays outside `TaskItemTags` so the summary chip cannot
                            hide it, and inside the measured right area so `showSummarizeTag` still
                            accounts for its width. */}
                        {trailingRoutingTag}
                        <TaskItemTags
                            task={task}
                            projectId={projectId}
                            isObservedTask={isObservedTask}
                            isToReviewTask={isToReviewTask}
                            isSuggested={isSuggested}
                            toggleSubTaskList={toggleSubTaskList}
                            subtaskList={subtaskList}
                            isActiveOrganizeMode={isActiveOrganizeMode}
                            accessGranted={accessGranted}
                            anonymousGranted={anonymousGranted}
                            forceTagsMobile={forceTagsMobile}
                            setTagsExpandedHeight={setTagsExpandedHeight}
                            isLocked={isLocked}
                            isPending={isPending}
                            setExpandTags={setExpandTags}
                            expandTags={expandTags}
                            showSummarizeTagInByTime={showSummarizeTag}
                            inCommentPopup={inCommentPopup}
                        />
                    </View>
                </View>
                {expandTags && (
                    <View style={localStyles.tagsGroup} onLayout={onLayoutInExpandedTagsArea}>
                        <Tags
                            task={task}
                            projectId={projectId}
                            isObservedTask={isObservedTask}
                            isToReviewTask={isToReviewTask}
                            toggleSubTaskList={toggleSubTaskList}
                            subtaskList={subtaskList}
                            isActiveOrganizeMode={isActiveOrganizeMode}
                            accessGranted={accessGranted}
                            anonymousGranted={anonymousGranted}
                            forceTagsMobile={forceTagsMobile}
                            isLocked={isLocked}
                            isSuggested={isSuggested}
                            isPending={isPending}
                            needSummarize={true}
                            tagsStyle={{ marginLeft: 0, marginRight: 8, marginBottom: 5 }}
                            inCommentPopup={inCommentPopup}
                        />
                    </View>
                )}
            </TouchableOpacity>
        </Animated.View>
    )
}

const localStyles = StyleSheet.create({
    taskTags: {
        position: 'absolute',
        right: 0,
        left: 44,
        top: 3,
    },
    innerTaskTags: {
        marginTop: 5,
        marginBottom: 5,
        paddingRight: 8,
        alignItems: 'flex-start',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
    },
    leftArea: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    innerSubtasksTags: {
        marginTop: 6,
        marginBottom: 6,
    },
    tagsGroup: {
        alignContent: 'flex-end',
        justifyContent: 'flex-end',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
})
